// The drawer's Money block (GRO-247): the figures that say what an opportunity is worth, so they
// can be compared side by side, with a tag on the one the Pipeline column uses (GRO-246).
//
// Salesforce's field labels aren't in the mirror. "Qualified pipeline (at stage 2)" is ours: the
// field is a copy of the amount frozen when the deal first reached stage 2 (sources.md).

import type { Money, Opportunity, PipelineSource } from '../api/types.ts';
import { hasAmount } from './format.ts';

export interface MoneyLine {
  key: keyof Money;
  label: string;
  value: number | null;
  // The figure `pipeline_value` came from, which the drawer tags "Pipeline".
  isPipeline: boolean;
}

// In the drawer's order. An `always` line shows "—" when empty; the others only when set.
const MONEY_FIELDS: Array<{ key: keyof Money; label: string; always: boolean }> = [
  { key: 'amount', label: 'Amount', always: true },
  { key: 'year_1_arr', label: 'Year 1 ARR', always: true },
  { key: 'qualified_pipeline', label: 'Qualified pipeline (at stage 2)', always: true },
  { key: 'net_new_bookings', label: 'Net new bookings', always: true },
  // The ARR being renewed: set on nearly every renewal and almost nothing else.
  { key: 'renewal_baseline', label: 'Renewal baseline', always: false },
];

// The label of the figure the Pipeline column shows, for the drawer's headline tile.
export const PIPELINE_SOURCE_LABELS: Record<PipelineSource, string> = {
  year_1_arr: 'Year 1 ARR',
  qualified_pipeline: 'Qualified pipeline (at stage 2)',
  amount: 'Amount',
};

export function moneyLines(opportunity: Opportunity): MoneyLine[] {
  const lines: MoneyLine[] = [];
  for (const field of MONEY_FIELDS) {
    const value = opportunity.money[field.key];
    if (!field.always && !hasAmount(value)) continue;
    lines.push({ key: field.key, label: field.label, value, isPipeline: opportunity.pipeline_source === field.key });
  }
  return lines;
}
